import React from 'react';
import { Helmet } from 'react-helmet';

const HomeSEO = () => {
    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>Data Connector — Stop Copying Sales &amp; Orders Between Apps</title>
            <meta name="title" content="Data Connector — Stop Copying Sales &amp; Orders Between Apps" />
            <meta name="description" content="We don't replace your spreadsheets—we keep them up to date. Fewer exports and weekly report rebuilds between Shopify, QuickBooks, and more." />
            <meta name="keywords" content="shopify quickbooks, sales orders spreadsheet, small business reporting, stop copying between apps, marketing numbers one place, connect business software" />
            <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
            <meta name="language" content="English" />
            <meta name="author" content="Data Connector Inc." />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <meta name="theme-color" content="#56B2E3" />
            <meta name="application-name" content="Data Connector" />
            <meta name="apple-mobile-web-app-title" content="Data Connector" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="default" />
            <meta name="format-detection" content="telephone=no" />
            
            {/* Canonical URL */}
            <link rel="canonical" href="https://dataconnector.com/" />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://dataconnector.com/" />
            <meta property="og:title" content="Data Connector — Stop Copying Between Your Apps" />
            <meta property="og:description" content="Same spreadsheets and weekly reports—up to date for you. Less export, less retyping across Shopify, QuickBooks, ads, and Sheets." />
            <meta property="og:image" content="https://dataconnector.com/og-image.png" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Data Connector — sales and orders up to date across your apps" />
            <meta property="og:site_name" content="Data Connector" />
            <meta property="og:locale" content="en_US" />
            <meta property="og:locale:alternate" content="es_ES" />
            <meta property="og:locale:alternate" content="fr_FR" />
            <meta property="og:locale:alternate" content="de_DE" />
            
            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content="https://dataconnector.com/" />
            <meta property="twitter:title" content="Data Connector — Stop Copying Between Your Apps" />
            <meta property="twitter:description" content="Fewer exports and pasted rows. Same spreadsheets—we keep them up to date across the apps you already use." />
            <meta property="twitter:image" content="https://dataconnector.com/og-image.png" />
            <meta property="twitter:image:alt" content="Data Connector — spreadsheets up to date across your apps" />
            <meta property="twitter:site" content="@dataconnector" />
            <meta property="twitter:creator" content="@dataconnector" />
            
            {/* Additional SEO Meta Tags */}
            <meta name="google-site-verification" content="your-verification-code" />
            <meta name="msvalidate.01" content="your-bing-verification-code" />
            <meta name="yandex-verification" content="your-yandex-verification-code" />
            
            {/* Performance Optimizations */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link rel="preconnect" href="https://www.google-analytics.com" />
            <link rel="preconnect" href="https://www.googletagmanager.com" />
            <link rel="dns-prefetch" href="//js-na1.hs-scripts.com" />
            <link rel="dns-prefetch" href="//www.googletagmanager.com" />
            <link rel="dns-prefetch" href="//www.google-analytics.com" />
            
            {/* Preload Critical Images */}
            <link rel="preload" as="image" href="https://dataconnector.com/og-image.png" />
            <link rel="preload" as="image" href="https://dataconnector.com/logo.png" />
            
            {/* Enhanced Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "SoftwareApplication",
                    "name": "Data Connector",
                    "applicationCategory": "BusinessApplication",
                    "operatingSystem": "Web",
                    "description": "Cut out exporting and retyping between Shopify, QuickBooks, ads, and spreadsheets. We don't replace your spreadsheets—we keep them up to date. Set up in minutes.",
                    "url": "https://dataconnector.com/",
                    "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "USD",
                        "availability": "https://schema.org/InStock",
                        "description": "Free trial with no build fees"
                    },
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": 4.8,
                        "ratingCount": 150,
                        "bestRating": 5,
                        "worstRating": 1
                    },
                    "provider": {
                        "@type": "Organization",
                        "name": "Data Connector Inc.",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "1633 East 550 South",
                            "addressLocality": "Springville",
                            "addressRegion": "UT",
                            "postalCode": "84663",
                            "addressCountry": "US"
                        }
                    },
                    "featureList": [
                        "Keep sales, orders, and customers up to date across your apps—updated for you",
                        "No coding required",
                        "Runs on a schedule you choose",
                        "Send numbers to Excel, CSV, and Google Sheets when you need files",
                        "Clear pricing"
                    ]
                })}
            </script>
            
            {/* WebSite Schema */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "Data Connector",
                    "url": "https://dataconnector.com/",
                    "description": "Stop copying sales and orders between Shopify, QuickBooks, and spreadsheets.",
                    "potentialAction": {
                        "@type": "SearchAction",
                        "target": {
                            "@type": "EntryPoint",
                            "urlTemplate": "https://dataconnector.com/search?q={search_term_string}"
                        },
                        "query-input": "required name=search_term_string"
                    }
                })}
            </script>
            
            {/* Organization Schema */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "Data Connector Inc.",
                    "url": "https://dataconnector.com/",
                    "logo": "https://dataconnector.com/logo.png",
                    "description": "Keep sales, orders, and weekly reports up to date across the apps small businesses already use.",
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "1633 East 550 South",
                        "addressLocality": "Springville",
                        "addressRegion": "UT",
                        "postalCode": "84663",
                        "addressCountry": "US"
                    },
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "contactType": "customer service",
                        "availableLanguage": "English",
                        "url": "https://dataconnector.com/support"
                    },
                    "sameAs": [
                        "https://www.linkedin.com/company/dataconnector",
                        "https://twitter.com/dataconnector"
                    ]
                })}
            </script>
            
            {/* HowTo Schema */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "HowTo",
                    "name": "How to Set Up Data Connector",
                    "description": "Step-by-step guide to get started with Data Connector",
                    "image": "https://dataconnector.com/og-image.png",
                    "totalTime": "PT10M",
                    "estimatedCost": {
                        "@type": "MonetaryAmount",
                        "currency": "USD",
                        "value": "0"
                    },
                    "supply": [
                        {
                            "@type": "HowToSupply",
                            "name": "Data Connector account"
                        }
                    ],
                    "tool": [
                        {
                            "@type": "HowToTool",
                            "name": "Data Connector"
                        }
                    ],
                    "step": [
                        {
                            "@type": "HowToStep",
                            "name": "Sign up for free trial",
                            "text": "Create your account in minutes. Simply provide your email and company information.",
                            "image": "https://dataconnector.com/og-image.png"
                        },
                        {
                            "@type": "HowToStep",
                            "name": "Connect your apps",
                            "text": "Sign in to the apps you already use. No coding required—we help finish setup so things run reliably.",
                            "image": "https://dataconnector.com/og-image.png"
                        },
                        {
                            "@type": "HowToStep",
                            "name": "Choose what to update",
                            "text": "Tell us what should land where—sales into QuickBooks, ad numbers into a dashboard, orders into a spreadsheet—and how often.",
                            "image": "https://dataconnector.com/og-image.png"
                        },
                        {
                            "@type": "HowToStep",
                            "name": "Let it run",
                            "text": "Spreadsheets and weekly reports stay up to date—updated for you. Less time exporting CSVs and rebuilding the same file.",
                            "image": "https://dataconnector.com/og-image.png"
                        }
                    ]
                })}
            </script>
            
            {/* FAQ Schema */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [
                        {
                            "@type": "Question",
                            "name": "What is Data Connector?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Data Connector cuts out busywork between apps you already use—sales, ad results, and books line up without exporting and retyping."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "How does Data Connector work?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "You connect your apps, choose where sales or orders should land, and we keep them up to date on a schedule—updated for you. Our team helps verify setup."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "Is Data Connector secure?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Yes, Data Connector maintains enterprise-grade security standards with end-to-end encryption and secure authentication methods. We are HIPAA and SOC 2 Type II compliant. Visit our security page for detailed compliance status."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "How much does Data Connector cost?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Data Connector offers a free trial with no fees to build new connections and no limits on the number of connections. We use transparent pricing - you only pay for what you use."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "Can I still use Excel or Google Sheets?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Yes. Keep the spreadsheet or weekly report you already built—we keep it up to date so you export less often."
                            }
                        }
                    ]
                })}
            </script>
            
            {/* Breadcrumb Schema */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": "https://dataconnector.com/"
                        }
                    ]
                })}
            </script>
            
            {/* Image Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ImageObject",
                    "contentUrl": "https://dataconnector.com/og-image.png",
                    "name": "Data Connector Platform",
                    "description": "Automate work between your business tools",
                    "width": 1200,
                    "height": 630,
                    "license": "https://dataconnector.com/terms",
                    "creator": {
                        "@type": "Organization",
                        "name": "Data Connector Inc."
                    }
                })}
            </script>
        </Helmet>
    );
};

export default HomeSEO; 